import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { CollinsUser } from "../types/user";
import { VenueGroupConfig } from "../types/VenueGroup";
import { getEnvironment } from "../utils/environment";

declare global {
  interface Window {
    Intercom: (command: string, options?: IntercomSettings | object) => void;
  }
}

interface IntercomSettings {
  app_id: string;
  user_id?: string;
  companies?: Array<{
    company_id: string;
    name: string;
  }>;
  name?: string;
  email?: string;
  created_at?: number;
  site?: string;
  last_request_at?: number;
}

interface UseIntercomProps {
  user?: CollinsUser;
  venueGroupConfig?: VenueGroupConfig | null;
  isAuthenticated: boolean;
}

export const useIntercom = ({
  user,
  venueGroupConfig,
  isAuthenticated
}: UseIntercomProps): void => {
  const location = useLocation();
  const hasBooted = useRef(false);

  useEffect(() => {
    const bootIntercom = async () => {
      if (!isAuthenticated || !user || hasBooted.current) {
        return;
      }

      const environment = await getEnvironment();
      const intercomAppId = environment?.INTERCOM_APP_ID;

      if (!intercomAppId || typeof window.Intercom !== "function") {
        return;
      }

      const companies: Array<{ company_id: string; name: string }> = [];
      if (venueGroupConfig?._id) {
        companies.push({
          company_id: venueGroupConfig._id,
          name: venueGroupConfig.title ?? ""
        });
      }

      const createdAt = user.created_date
        ? Math.floor(new Date(user.created_date).getTime() / 1000)
        : undefined;

      window.Intercom("boot", {
        app_id: intercomAppId,
        user_id: user.workspace_id,
        companies,
        name: `${user.first_name ?? ""} ${user.last_name ?? ""}`.trim(),
        email: user.email,
        created_at: createdAt
      });

      hasBooted.current = true;
    };

    bootIntercom();
  }, [isAuthenticated, user, venueGroupConfig]);

  useEffect(() => {
    if (!hasBooted.current || typeof window.Intercom !== "function") {
      return;
    }

    const updateIntercom = async () => {
      const environment = await getEnvironment();
      const intercomAppId = environment?.INTERCOM_APP_ID;

      if (!intercomAppId) {
        return;
      }

      const pathParts = location.pathname.split("/");
      const siteIndex = pathParts.indexOf("sites");
      const venueId =
        siteIndex !== -1 && pathParts.length > siteIndex + 1
          ? pathParts[siteIndex + 1]
          : undefined;

      if (venueId) {
        window.Intercom("update", {
          app_id: intercomAppId,
          user_id: user?.workspace_id,
          site: venueId,
          email: user?.email
        });
      } else {
        window.Intercom("update", {
          last_request_at: Math.floor(Date.now() / 1000)
        });
      }
    };

    updateIntercom();
  }, [location.pathname, user?.workspace_id, user?.email]);
};
